import React from 'react';
import {Pressable, StyleSheet, View} from 'react-native';
import {Icon} from '@rneui/themed';

import {universal} from '../../../design-system/tokens/colour';
import {space} from '../../../design-system/tokens/spacing';
import theme from '../../../style/theme';
import Text from '../../themed/Text';

export type CheckBoxProps = {
  checked: boolean;
  onChange: (value: boolean) => void;
  /**
   * A string is wrapped in the themed body2 Text. Anything else is rendered as
   * given, so a caller can supply its own column - a label plus supporting copy,
   * or a sentence carrying a link - and keep it in one text column beside the
   * box. Both production callers already pass a node rather than a string.
   */
  text: React.ReactNode;
  /** Applied to the label only while unchecked, e.g. an error tint. */
  uncheckedStyles?: {textStyle: Record<string, unknown>};
  testID?: string;
};

/**
 * The rewards opt-in and consent checkbox.
 *
 * The box is drawn here rather than delegated to a platform control. The
 * previous implementation used @react-native-community/checkbox, which has no
 * size prop, and on web resolves to ui-controls/web/CheckBox.tsx - a bare
 * `<input type="checkbox">` rendered at the browser's default size, which is
 * why the box read as small there and different again on each platform. Drawing
 * it is the only way to hold one size across web, iOS and Android.
 *
 * The row is top-aligned, not centre-aligned: both callers pass a label that
 * wraps to more than one line, and centring puts the box against the middle of
 * the paragraph instead of against its first line.
 */
export function CheckBox({
  checked = false,
  onChange,
  text,
  uncheckedStyles = {textStyle: {}},
  testID = 'checkbox',
}: CheckBoxProps) {
  return (
    <Pressable
      style={styles.row}
      onPress={() => onChange(!checked)}
      accessibilityRole="checkbox"
      accessibilityState={{checked}}
      testID={testID}>
      <View style={[styles.box, checked && styles.boxChecked]} testID={`${testID}-box`}>
        {checked ? (
          // @rneui hardcodes accessibilityRole="button" on its icon container,
          // which react-native-web renders as a real <button>. Nested inside the
          // Pressable that is invalid HTML and a second tab stop for one
          // control, so override it - the Pressable carries the role.
          <Icon
            name="check"
            type="material"
            size={CHECK_SIZE}
            color={theme.colour.background.button.contrastOnPrimary}
            accessibilityRole="none"
          />
        ) : null}
      </View>
      <View style={styles.label}>
        {typeof text === 'string' ? (
          <Text variant="body2" style={checked ? undefined : uncheckedStyles.textStyle}>
            {text}
          </Text>
        ) : (
          text
        )}
      </View>
    </Pressable>
  );
}

// 20x20, 1px #757575, 2px radius and the white fill are the values the design
// supplies for this control. The border is universal.greyDark (#757575), a
// brand-neutral grey that must NOT be repointed at a per-brand theme colour -
// all four brands draw the same box. The checked fill below is theme-resolved,
// so the tick sits on brand primary.
// SHORTCUT: BOX_SIZE 20, CHECK_SIZE 16 and the box's borderRadius 2 stay
// literals: no size or radius token equals them. Ceiling: this checkbox only.
// Upgrade path: control-size tokens under app/design-system/tokens/ - DIS-1735
// is the epic.
const BOX_SIZE = 20;
const CHECK_SIZE = 16;
const BOX_BORDER_COLOUR = universal.greyDark;
const LABEL_GAP = space.s8;

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  box: {
    width: BOX_SIZE,
    height: BOX_SIZE,
    borderRadius: 2,
    borderWidth: 1,
    borderColor: BOX_BORDER_COLOUR,
    // The design's white fill. `tile.surface` is LMG_WHITE in all four brand
    // themes; `general.contrastOnPrimary` is LMG_BLACK on Thirsty Camel and
    // drew a solid black unchecked box there (DIS-1765).
    backgroundColor: theme.colour.background.tile.surface,
    alignItems: 'center',
    justifyContent: 'center',
  },
  boxChecked: {
    backgroundColor: theme.colour.checkbox.primary,
    borderColor: theme.colour.checkbox.primary,
  },
  label: {
    flex: 1,
    marginLeft: LABEL_GAP,
  },
});
